import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PendingApprovalKit, PendingDetails, PendingDetailsItem } from '../types';
import { StyledDialogFooter, StyledDialogTitle, StyledLargeDialogContent } from '../../../../components/Dialog.styled';
import { getPendingDetails, getTaxRate, sendRefillRequest } from '../shipmentService';
import { addError } from '../../../../redux/alerts';
import constants from '../../../../constants';
import { standardDateFormat } from '../../../../helpers/date';
import ExpiredConfirmationModal from './ExpiredConfirmationModal';
import { useAppDispatch } from '../../../../redux/store';
import LoadingSection from '../../../../components/LoadingSection';
import { CustomerState } from '../../../../redux/customer';
import { ChildCustomerState } from '../../../../redux/childCustomer';

type CategoryKey = 'backorderedItems' | 'orderProcessingItems' | 'recommendedItems' | 'untrackedItems' | 'upToDateItems';

type Step = 1 | 2 | 3;

interface FormState {
  enrollInAuto: boolean;
  paymentMethod: string;
  usePurchaseOrder: boolean;
  purchaseOrderNumber: string | null;
  approverName: string;
}

interface PendingSummaryState {
  backorderedItems: PendingDetailsItem[];
  orderProcessingItems: PendingDetailsItem[];
  recommendedItems: PendingDetailsItem[];
  untrackedItems: PendingDetailsItem[];
  upToDateItems: PendingDetailsItem[];
}

interface Props {
  customer: CustomerState | ChildCustomerState;
  open: boolean;
  kit: PendingApprovalKit;
  onClose: () => void;
  onSuccess: () => void;
}

const initialFormState: FormState = {
  enrollInAuto: false,
  paymentMethod: 'Net on receipt',
  usePurchaseOrder: false,
  purchaseOrderNumber: null,
  approverName: ''
};

const initalPendingSummaryState: PendingSummaryState = {
  backorderedItems: [],
  orderProcessingItems: [],
  recommendedItems: [],
  untrackedItems: [],
  upToDateItems: []
};

const ApprovalModal = ({ customer, open, kit, onClose, onSuccess }: Props) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const today = dayjs();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [pendingDetails, setPendingDetails] = useState<PendingDetails | null>(null);
  const [pendingSummary, setpendingSummary] = useState<PendingSummaryState>(initalPendingSummaryState);
  const [taxRate, setTaxRate] = useState<number>(0);
  const [form, setForm] = useState<FormState>(initialFormState);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PendingDetailsItem | null>(null);

  const getDetails = async () => {
    setLoading(true);
    try {
      const details = await getPendingDetails(kit);
      setPendingDetails(details);

      const taxes = await getTaxRate(customer);
      setTaxRate(taxes);
      setLoading(false);
    } catch (error) {
      dispatch(addError(error instanceof Error ? error.message : 'There was an error retrieving some data, please try again later.'));
      setLoading(false);
    }
  };

  const setItemRefill = (item: PendingDetailsItem) => {
    const category = item?.category;
    const allowedKeys: CategoryKey[] = ['recommendedItems', 'untrackedItems', 'upToDateItems'];

    if (!allowedKeys.includes(category as CategoryKey)) {
      dispatch(addError('Invalid category selection'));
      return;
    }

    if (item.is_expired && item.refill) {
      setSelectedItem(item);
      setShowExpiredModal(true);
      return;
    }

    updateItemRefill(item);
  };

  const getPrice = (price: number | null) => {
    if (!price) return 0.00;

    return price.toFixed(2);
  }

  const updateItemRefill = (item: PendingDetailsItem) => {
    const category = item?.category;
    const matchedItem = pendingSummary[category as CategoryKey]?.find((match) => item.item_number === match.item_number);

    if (matchedItem) {
      const updatedItem = { ...matchedItem, refill: !matchedItem.refill };

      setpendingSummary((state) => {
        if (!state) return state;

        const updatedItems = state[category as CategoryKey].map((i) => (i.item_number === updatedItem.item_number ? updatedItem : i));

        return {
          ...state,
          [category as CategoryKey]: updatedItems
        };
      });
    }
  };

  const setTrackingStatus = (item: PendingDetailsItem) => {
    const category = item?.category;

    const allowedKeys: CategoryKey[] = ['untrackedItems'];

    if (!allowedKeys.includes(category as CategoryKey)) {
      dispatch(addError('Invalid category selection'));
      return;
    }

    const matchedItem = pendingSummary[category as CategoryKey]?.find((match) => item.item_number === match.item_number);

    if (matchedItem) {
      const updatedItem = { ...matchedItem, track: !matchedItem.track };

      setpendingSummary((state) => {
        if (!state) return state;

        const updatedItems = state[category as CategoryKey].map((i) => (i.item_number === updatedItem.item_number ? updatedItem : i));

        return {
          ...state,
          [category as CategoryKey]: updatedItems
        };
      });
    }
  };

  const submitRefill = async () => {
    const refillData: Partial<PendingDetails> | null = structuredClone(pendingDetails) ?? {};
    if (refillData?.items) {
      delete refillData.items;
    }

    const { approverName, enrollInAuto, paymentMethod, usePurchaseOrder, purchaseOrderNumber } = form;

    const payload = {
      approvedItems: approvedRefillItems.map(({ item_number, quantity, price }) => ({ item_number, quantity, price })),
      approverName,
      enrollInAuto,
      gpKitId: kit.GP_KitId,
      kitId: kit.kit_id,
      paymentMethod,
      purchaseOrderNumber: usePurchaseOrder ? purchaseOrderNumber : null,
      refillData: refillData,
      tax: taxTotal,
      trackedItems
    };

    try {
      setLoading(true);

      await sendRefillRequest(payload, customer.customer_number);
      setLoading(false);
      onSuccess();
    } catch (error) {
      dispatch(addError('There was a problem submitting your refill request, please try again later.'));
      setLoading(false);
    }
  };

  const handleChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setForm((state) => ({ ...state, [name]: checked }));
  };

  const handleFormChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setForm((state) => ({ ...state, [name]: value }));
  };

  const alreadyApproved = useMemo(() => {
    if (!kit.pending_approval || pendingDetails?.items.length === 0) {
      return true;
    }
    return false;
  }, [pendingDetails]);

  const approvedRefillItems = useMemo(() => {
    return [...pendingSummary.recommendedItems, ...pendingSummary.upToDateItems, ...pendingSummary.untrackedItems]
      .filter((item) => item.refill)
      .sort((a, b) => {
        const nameA = a.name?.toLowerCase() ?? '';
        const nameB = b.name?.toLowerCase() ?? '';
        return nameA.localeCompare(nameB);
      });
  }, [pendingSummary.recommendedItems, pendingSummary.upToDateItems, pendingSummary.untrackedItems]);

  const approvedRefillTotal = useMemo(() => {
    return approvedRefillItems.reduce((acc, item) => {
      return (item.price ?? 0) * item.quantity + acc;
    }, 0);
  }, [approvedRefillItems]);

  const taxTotal = useMemo(() => {
    if (!customer.is_taxable || taxRate === 0 || approvedRefillTotal === 0) return 0.0;

    const { dscsa_fee = 0, unused_med_fee = 0 } = pendingDetails ?? {};

    const subtotal = approvedRefillTotal + dscsa_fee + unused_med_fee;
    const taxAmount = (subtotal * (taxRate / 100)).toFixed(2);

    return taxAmount;
  }, [taxRate, approvedRefillTotal, pendingDetails?.dscsa_fee, pendingDetails?.unused_med_fee]);

  const total = useMemo(() => {
    if (approvedRefillTotal === 0) return 0.0;
    const { shipping_fee = 0, dscsa_fee = 0, unused_med_fee = 0 } = pendingDetails ?? {};

    return (approvedRefillTotal + unused_med_fee + dscsa_fee + shipping_fee + Number(taxTotal)).toFixed(2);
  }, [approvedRefillTotal, taxTotal, pendingDetails?.shipping_fee, pendingDetails?.dscsa_fee, pendingDetails?.unused_med_fee]);

  const trackedItems = useMemo(() => {
    return pendingSummary.untrackedItems.filter((item) => item.track).map((item) => item.item_number);
  }, [pendingSummary.untrackedItems]);

  useEffect(() => {
    if (!pendingDetails?.items) return;

    const backorderedItems: PendingDetailsItem[] = [];
    const orderProcessingItems: PendingDetailsItem[] = [];
    const recommendedItems: PendingDetailsItem[] = [];
    const upToDateItems: PendingDetailsItem[] = [];
    const untrackedItems: PendingDetailsItem[] = [];

    pendingDetails.items.forEach((item) => {
      if (item.is_backordered) {
        item.refill = false;
        item.category = 'backorderedItems';
        backorderedItems.push(item);
      } else if (item.compliance_code === 'EPREP_DEVICE_ORDER_PROCESSING' || item.compliance_code === 'EPREP_MED_ORDER_PROCESSING') {
        item.refill = false;
        item.category = 'orderProcessingItems';
        orderProcessingItems.push(item);
      } else if (item.is_recommended === true || (dayjs(item.expiration_date).isBefore(today) && item.is_tracked === true)) {
        item.refill = true;
        item.category = 'recommendedItems';
        recommendedItems.push(item);
      } else if (item.is_tracked === true) {
        item.refill = false;
        item.category = 'upToDateItems';
        upToDateItems.push(item);
      } else {
        item.refill = false;
        item.track = false;
        item.category = 'untrackedItems';
        untrackedItems.push(item);
      }
    });

    setpendingSummary({
      backorderedItems,
      orderProcessingItems,
      recommendedItems,
      untrackedItems,
      upToDateItems
    });
  }, [pendingDetails]);

  const refillSummary = useMemo(
    () => [
      {
        category: 'Refilled Items',
        total: approvedRefillTotal.toFixed(2) ?? 0.0,
        sx: { fontWeight: 'bold' }
      },
      {
        category: 'DSCSA Service',
        total: approvedRefillItems.length > 0 ? pendingDetails?.dscsa_fee?.toFixed(2) ?? 0.0 : 0.0,
        sx: {}
      },
      {
        category: 'Unused Medications Fee',
        total: approvedRefillItems.length > 0 ? pendingDetails?.unused_med_fee?.toFixed(2) ?? 0.0 : 0.0,
        sx: {}
      },
      {
        category: 'Shipping',
        total: approvedRefillItems.length > 0 ? pendingDetails?.shipping_fee?.toFixed(2) ?? 0.0 : 0.0,
        sx: {}
      },
      {
        category: 'Tax¹',
        total: taxTotal ?? 0.0,
        sx: {}
      },
      {
        category: 'Total',
        total: total ?? 0.0,
        sx: { fontWeight: 'bold', fontSize: '18px' }
      }
    ],
    [approvedRefillTotal, approvedRefillItems.length, pendingDetails, taxTotal, total]
  );

  useEffect(() => {
    if (kit.customer_number) {
      getDetails();
    }
  }, [kit]);

  return (
    <>
      <Dialog maxWidth="lg" fullWidth={true} open={open}>
        <StyledDialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Refill Your Kit</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </StyledDialogTitle>
        {!loading && !alreadyApproved && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '17px',
              fontWeight: 'bold',
              p: 2,
              fontFamily: "'Open Sans', sans-serif",
              borderBottom: 1,
              borderBottomColor: theme.palette.colordee5e7.main,
              color: theme.palette.color50555b.main
            }}
          >
            <div className="m-r-16" style={{ color: step === 1 ? '' : theme.palette.inactive.main }}>
              Step 1: Select Items to Refill
            </div>
            <div className="m-r-16 flex flex-align-center" style={{ color: step === 2 ? '' : theme.palette.inactive.main }}>
              <ArrowForwardIosIcon /> <span className="m-l-12">Step 2: Payment Information</span>
            </div>
            <div className="m-r-16 flex flex-align-center" style={{ color: step === 3 ? '' : theme.palette.inactive.main }}>
              <ArrowForwardIosIcon /> <span className="m-l-12">Step 3: Review and Approve</span>
            </div>
          </Box>
        )}
        <StyledLargeDialogContent sx={{ minHeight: '500px' }}>
          {loading && (
            <LoadingSection />
          )}
          {alreadyApproved && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, color: theme.palette.success.main }} component="span">
                  <CheckCircleIcon fontSize="large" />
                </Box>
                <span className="f-s-33">Refill Already Approved</span>
              </Box>
              <div>The refill for this kit was already approved. Please allow 2-3 weeks from the time of approval for delivery.</div>
            </Box>
          )}
          {!loading && !alreadyApproved && step === 1 && (
            <Box>
              <Card sx={{ mb: 2 }}>
                {/* Recommended Items */}
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="50%">Items</TableCell>
                      <TableCell width="10%">Expiration</TableCell>
                      <TableCell width="10%">Unit Price</TableCell>
                      <TableCell width="5%">Qty</TableCell>
                      <TableCell width="5%" sx={{ borderLeft: 1, borderLeftColor: theme.palette.inactive.main }}>
                        Refill
                      </TableCell>
                      <TableCell width="10%">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingSummary.recommendedItems
                      .slice()
                      .sort((a, b) => new Date(a.expiration_date).getTime() - new Date(b.expiration_date).getTime())
                      .map((item, i) => (
                        <TableRow key={i} sx={{ backgroundColor: item.is_expired ? theme.palette.backgroundDanger.main : '' }}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{standardDateFormat(item.expiration_date)}</TableCell>
                          <TableCell>${getPrice(item.price)}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell sx={{ borderLeft: 1, borderLeftColor: theme.palette.inactive.main }}>
                            <Checkbox checked={item.refill} onChange={() => setItemRefill(item)} />
                          </TableCell>
                          <TableCell>
                            {item.refill ? (
                              `$${((item.price ?? 0) * item.quantity).toFixed(2)}`
                            ) : (
                              <Box sx={{ color: theme.palette.inactive.main }} component="span">
                                $0.00
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                {/* Order Processing Items */}
                {pendingSummary.orderProcessingItems.length > 0 && (
                  <Accordion disableGutters sx={{ color: theme.palette.color50555b.main }}>
                    <AccordionSummary sx={{ backgroundColor: theme.palette.backgroundGray.main }} expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }} component="span">
                          Order Processing Items
                        </Box>
                        <Tooltip title="These items have a refill still being processed at this time.">
                          <InfoIcon fontSize="small" />
                        </Tooltip>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Table>
                        <TableBody>
                          {pendingSummary.orderProcessingItems
                            .slice()
                            .sort((a, b) => new Date(a.expiration_date).getTime() - new Date(b.expiration_date).getTime())
                            .map((item, i) => (
                              <TableRow key={i}>
                                <TableCell width="50%" sx={{ color: theme.palette.inactive.main }}>
                                  {item.name}
                                </TableCell>
                                <TableCell width="10%" sx={{ color: theme.palette.inactive.main }}>
                                  {standardDateFormat(item.expiration_date)}
                                </TableCell>
                                <TableCell width="10%" sx={{ color: theme.palette.inactive.main }}>
                                  ${getPrice(item.price)}
                                </TableCell>
                                <TableCell width="5%" sx={{ color: theme.palette.inactive.main }}>
                                  {item.quantity}
                                </TableCell>
                                <TableCell width="5%" sx={{ borderLeft: 1, borderLeftColor: theme.palette.inactive.main }}>
                                  <Checkbox checked={item.refill} disabled />
                                </TableCell>
                                <TableCell width="10%"></TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}
                {/* Up to Date Items */}
                {pendingSummary.upToDateItems.length > 0 && (
                  <Accordion disableGutters sx={{ color: theme.palette.color50555b.main }}>
                    <AccordionSummary sx={{ backgroundColor: theme.palette.backgroundGray.main }} expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }} component="span">
                          Up to Date Items
                        </Box>
                        <Tooltip title="These items are up to date and do not need to be refilled at this time.">
                          <InfoIcon fontSize="small" />
                        </Tooltip>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Table>
                        <TableBody>
                          {pendingSummary.upToDateItems
                            .slice()
                            .sort((a, b) => new Date(a.expiration_date).getTime() - new Date(b.expiration_date).getTime())
                            .map((item, i) => (
                              <TableRow key={i}>
                                <TableCell width="50%">{item.name}</TableCell>
                                <TableCell width="10%">{standardDateFormat(item.expiration_date)}</TableCell>
                                <TableCell width="10%">${getPrice(item.price)}</TableCell>
                                <TableCell width="5%">{item.quantity}</TableCell>
                                <TableCell width="5%" sx={{ borderLeft: 1, borderLeftColor: theme.palette.inactive.main }}>
                                  <Checkbox checked={item.refill} onChange={() => setItemRefill(item)} />
                                </TableCell>
                                <TableCell width="10%">
                                  {item.refill ? (
                                    `$${((item.price ?? 0) * item.quantity).toFixed(2)}`
                                  ) : (
                                    <Box sx={{ color: theme.palette.inactive.main }} component="span">
                                      $0.00
                                    </Box>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}
                {/* Backordered Items */}
                {pendingSummary.backorderedItems.length > 0 && (
                  <Accordion disableGutters sx={{ color: theme.palette.color50555b.main }}>
                    <AccordionSummary sx={{ backgroundColor: theme.palette.backgroundGray.main }} expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }} component="span">
                          Backordered Items
                        </Box>
                        <Tooltip title="These items are currently backordered and cannot be refilled at this time.">
                          <InfoIcon fontSize="small" />
                        </Tooltip>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Table>
                        <TableBody>
                          {pendingSummary.backorderedItems
                            .slice()
                            .sort((a, b) => new Date(a.expiration_date).getTime() - new Date(b.expiration_date).getTime())
                            .map((item, i) => (
                              <TableRow key={i}>
                                <TableCell width="50%" sx={{ color: theme.palette.inactive.main }}>
                                  {item.name}
                                </TableCell>
                                <TableCell width="10%" sx={{ color: theme.palette.inactive.main }}>
                                  {standardDateFormat(item.expiration_date)}
                                </TableCell>
                                <TableCell width="10%" sx={{ color: theme.palette.inactive.main }}>
                                  ${getPrice(item.price)}
                                </TableCell>
                                <TableCell width="5%" sx={{ color: theme.palette.inactive.main }}>
                                  {item.quantity}
                                </TableCell>
                                <TableCell width="5%"></TableCell>
                                <TableCell width="10%"></TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}
                {/* Untracked Items */}
                {pendingSummary.untrackedItems.length > 0 && (
                  <Accordion disableGutters sx={{ color: theme.palette.color50555b.main }}>
                    <AccordionSummary sx={{ backgroundColor: theme.palette.backgroundGray.main }} expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }} component="span">
                          Items Not Being Refilled Per Customer's Request
                        </Box>
                        <Tooltip title="Per your request, HealthFirst will not notify you when these items are expiring.">
                          <InfoIcon fontSize="small" />
                        </Tooltip>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Table>
                        <TableBody>
                          {pendingSummary.untrackedItems
                            .slice()
                            .sort((a, b) => new Date(a.expiration_date).getTime() - new Date(b.expiration_date).getTime())
                            .map((item, i) => (
                              <TableRow key={i} sx={{ backgroundColor: item.is_expired ? theme.palette.backgroundDanger.main : '' }}>
                                <TableCell width="50%">
                                  {item.name}{' '}
                                  <span className="link" onClick={() => setTrackingStatus(item)}>
                                    {item.track ? 'Cancel' : 'Track this item'}
                                  </span>
                                </TableCell>
                                <TableCell width="10%">{standardDateFormat(item.expiration_date)}</TableCell>
                                <TableCell width="10%">{getPrice(item.price)}</TableCell>
                                <TableCell width="5%">{item.quantity}</TableCell>
                                <TableCell width="5%" sx={{ borderLeft: 1, borderLeftColor: theme.palette.inactive.main }}>
                                  <Checkbox checked={item.refill} onChange={() => setItemRefill(item)} />
                                </TableCell>
                                <TableCell width="10%">
                                  {item.refill ? (
                                    `$${((item.price ?? 0) * item.quantity).toFixed(2)}`
                                  ) : (
                                    <Box sx={{ color: theme.palette.inactive.main }} component="span">
                                      $0.00
                                    </Box>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}
                {/* Summary */}
                <Table size="small">
                  <TableBody>
                    {refillSummary.map(({ category, total, sx }, i) => (
                      <TableRow key={i}>
                        <TableCell width="90%" sx={{ ...sx }}>
                          {category}
                        </TableCell>
                        <TableCell width="10%" sx={{ ...sx }}>
                          ${total}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          )}
          {!loading && step === 2 && (
            <Box>
              <Box sx={{ backgroundColor: theme.palette.backgroundGray.main, p: 2, mb: 2 }}>
                You will be sent an invoice with your refill. Please contact us upon receipt of your refill to process payment.
              </Box>
              <Card sx={{ p: 2, mb: 2, display: 'flex', alignContent: 'center' }}>
                <span className="m-r-16 flex flex-align-center">
                  <Checkbox name="usePurchaseOrder" checked={form.usePurchaseOrder} onChange={handleChecked} /> I have a purchase order
                </span>
                {form.usePurchaseOrder && (
                  <TextField
                    sx={{ minWidth: '210px' }}
                    value={form.purchaseOrderNumber ?? ''}
                    size="small"
                    placeholder="Purchase Order Number"
                    inputProps={{ maxLength: 25 }}
                    name="purchaseOrderNumber"
                    onChange={handleFormChange}
                  />
                )}
              </Card>
            </Box>
          )}
          {!loading && step === 3 && (
            <Box sx={{ mb: 2 }}>
              <Card sx={{ mb: 2 }}>
                <Accordion disableGutters sx={{ color: theme.palette.color50555b.main }}>
                  <AccordionSummary sx={{ backgroundColor: theme.palette.backgroundGray.main }} expandIcon={<ExpandMoreIcon />}>
                    See refill item summary
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell width="60%">Items</TableCell>
                          <TableCell width="10%">Unit Price</TableCell>
                          <TableCell width="10%">Qty</TableCell>
                          <TableCell width="20%">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {approvedRefillItems.map((item, i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ color: theme.palette.inactive.main }}>{item.name}</TableCell>
                            <TableCell sx={{ color: theme.palette.inactive.main }}>${getPrice(item.price)}</TableCell>
                            <TableCell sx={{ color: theme.palette.inactive.main }}>{item.quantity}</TableCell>
                            <TableCell sx={{ color: theme.palette.inactive.main }}>${((item.price ?? 0) * item.quantity).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
                <Table>
                  <TableBody>
                    {refillSummary.map(({ category, total, sx }, i) => (
                      <TableRow key={i}>
                        <TableCell width="80%" sx={{ ...sx }}>
                          {category}
                        </TableCell>
                        <TableCell width="20%" sx={{ ...sx }}>
                          ${total}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '18px' }}>Payment Method</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                        Invoice Me
                        {form.usePurchaseOrder && form.purchaseOrderNumber && <div>PO Number: {form.purchaseOrderNumber}</div>}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
              <Card sx={{ display: 'flex', justifyContent: 'space-between', p: 2, color: theme.palette.color50555b.main }}>
                <TextField
                  sx={{ minWidth: '400px' }}
                  value={form.approverName}
                  size="small"
                  placeholder="Name"
                  label="To approve this refill, please enter your name"
                  inputProps={{ maxLength: 50 }}
                  name="approverName"
                  onChange={handleFormChange}
                  error={Boolean(form.approverName === '')}
                />
                <span className="m-l-16 flex flex-align-center">
                  <Checkbox name="enrollInAuto" checked={form.enrollInAuto} onChange={handleChecked} /> Recommended: Sign Up for Automatic Refills
                  <Tooltip
                    sx={{ ml: 1 }}
                    title="Sign up to receive automatic shipments to replace items in this kit as expirations are nearing. You can view all Upcoming Shipments in OnTraq and will receive an email before shipments are sent and can make changes as needed."
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </span>
              </Card>
            </Box>
          )}
          {!loading && !alreadyApproved && (
            <Box sx={{ fontSize: '13px' }}>
              <div>For information or assistance, please click the Chat Now button below. Otherwise, feel free to contact us:</div>
              <ul className="m-t-0">
                <li>Phone: {constants.HEALTHFIRST_PHONE_NUMBER}</li>
                <li>
                  Email:{' '}
                  <a href={`mailto:${constants.ORDER_APPROVAL_EMAIL}`} className="link">
                    {constants.ORDER_APPROVAL_EMAIL}
                  </a>
                </li>
              </ul>
              <Box>¹ Tax amount may vary</Box>
            </Box>
          )}
        </StyledLargeDialogContent>
        <StyledDialogFooter>
          {step === 1 && (
            <>
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              {!alreadyApproved && (
                <Button onClick={() => setStep(2)} variant="contained" disabled={approvedRefillItems.length === 0}>
                  Next: Payment Method
                </Button>
              )}
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outlined" onClick={() => setStep(1)}>
                Back: Select Items
              </Button>
              <Button onClick={() => setStep(3)} variant="contained" disabled={form.usePurchaseOrder && !!!form.purchaseOrderNumber}>
                Next: Review
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button variant="outlined" onClick={() => setStep(1)}>
                Change Refilled Items
              </Button>
              <Button variant="outlined" onClick={() => setStep(2)}>
                Change Payment Method
              </Button>
              <Button
                color="success"
                sx={{ color: '#ffffff' }}
                variant="contained"
                onClick={() => submitRefill()}
                disabled={Boolean(form.approverName === '')}
              >
                Approve Refill
              </Button>
            </>
          )}
        </StyledDialogFooter>
      </Dialog>
      <ExpiredConfirmationModal
        open={showExpiredModal}
        handleClose={() => {
          setShowExpiredModal(false);
          setSelectedItem(null);
        }}
        handleSubmit={() => {
          if (selectedItem) {
            updateItemRefill(selectedItem);
          }
          setShowExpiredModal(false);
          setSelectedItem(null);
        }}
      />
    </>
  );
};

export default ApprovalModal;
